import { Compartment } from '@codemirror/state';
import {
  EditorView,
  ViewPlugin,
  type PluginValue,
  type ViewUpdate,
} from '@codemirror/view';
import { type RefObject } from 'react';

/**
 * Attribute used to expose the placeholder text on the content element so it can
 * be rendered through a CSS `::after` pseudo-element.
 */
export const placeholderDataAttribute = 'data-placeholder';

/**
 * Attribute set on the scroll element while a placeholder is visible, used to
 * scope the styles that grow the line and gutter to fit it.
 */
export const placeholderReservedAttribute = 'data-placeholder-reserved';

/**
 * Custom property carrying the measured placeholder height (in pixels). It is
 * set on the scroll element so both the content and the gutter can read it.
 */
export const placeholderHeightVariable = '--dt-editor-placeholder-height';

/**
 * Creates an extension that exposes the placeholder text as the
 * `data-placeholder` attribute on the editor's content element while the
 * document is empty.
 *
 * Avoids CodeMirror's built-in `placeholder` extension: it inserts a widget
 * into `contentEditable`, causing the browser to move the caret to the end of
 * the widget on blur. A CSS `::after` pseudo-element keeps the caret at
 * position 0.
 *
 * The pseudo-element is absolutely positioned so it wraps to the editor width
 * even when line wrapping is disabled, but does not contribute to layout height.
 * To compensate, we measure its rendered height and publish it as
 * `placeholderHeightVariable` on the scroll element; the stylesheet then grows
 * the empty line and gutter to match.
 */
const createPlaceholderExtension = (placeholderText: string) =>
  ViewPlugin.fromClass(
    class implements PluginValue {
      private readonly contentDOM: HTMLElement;
      private readonly scrollDOM: HTMLElement;
      private reservedHeight = '';

      constructor(view: EditorView) {
        this.contentDOM = view.contentDOM;
        this.scrollDOM = view.scrollDOM;
        this.sync(view);
      }

      update(update: ViewUpdate): void {
        if (update.docChanged) {
          this.sync(update.view);
        } else if (update.geometryChanged) {
          // A width change re-wraps the placeholder, so its reserved height has
          // to be recomputed while the placeholder is visible.
          this.reserveHeight(update.view);
        }
      }

      destroy(): void {
        this.contentDOM.removeAttribute(placeholderDataAttribute);
        this.setReservedHeight('');
      }

      private sync(view: EditorView): void {
        if (view.state.doc.length === 0) {
          this.contentDOM.setAttribute(
            placeholderDataAttribute,
            placeholderText,
          );
          this.reserveHeight(view);
        } else {
          this.contentDOM.removeAttribute(placeholderDataAttribute);
          this.setReservedHeight('');
        }
      }

      /**
       * Measures the rendered placeholder pseudo-element and publishes its
       * height so the stylesheet can grow the line and gutter to fit it. No-op
       * once the document has content.
       */
      private reserveHeight(view: EditorView): void {
        if (view.state.doc.length !== 0) {
          return;
        }
        view.requestMeasure({
          read: () => {
            const after = getComputedStyle(this.contentDOM, '::after');
            const placeholderHeight = Math.ceil(parseFloat(after.height) || 0);
            return placeholderHeight ? `${placeholderHeight}px` : '';
          },
          write: (height) => {
            this.setReservedHeight(height);
          },
        });
      }

      private setReservedHeight(height: string): void {
        if (height) {
          if (height === this.reservedHeight) {
            return;
          }
          this.reservedHeight = height;
          this.scrollDOM.style.setProperty(placeholderHeightVariable, height);
          this.scrollDOM.setAttribute(placeholderReservedAttribute, '');
        } else {
          // Clear unconditionally: typed content must never keep the reserved
          // line/gutter styling, even if the attribute was applied outside this
          // plugin instance's tracked state.
          this.reservedHeight = '';
          this.scrollDOM.style.removeProperty(placeholderHeightVariable);
          this.scrollDOM.removeAttribute(placeholderReservedAttribute);
        }
      }
    },
  );

/**
 * Configures the placeholder functionality on the editor.
 * @param placeholderText - string to be used as placeholder text for editor
 * @param viewRef - Ref object for the editor
 * @param placeholderCompartment - compartment used to reconfigure the placeholder extension
 */
export const configurePlaceholder = (
  placeholderText: string | undefined,
  viewRef: RefObject<EditorView>,
  placeholderCompartment: Compartment,
): void => {
  const editorView = viewRef.current;
  editorView.dispatch({
    effects: placeholderCompartment.reconfigure(
      placeholderText ? createPlaceholderExtension(placeholderText) : [],
    ),
  });
};
